import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Trash2, Undo2, ShieldAlert, Clock, User, Layers, X, Loader2, CheckSquare, Square } from "lucide-react";

const PRIMARY_LABEL = {
  program_booking: "Program booking",
  weekend_booking: "Weekend booking",
  arrival: "Arrival",
  trip: "Trip",
  cform: "C-Form",
  room_allocation: "Room allocation",
  issue: "Issue",
  task: "Task",
  partner: "Partner",
  sale_standalone: "Standalone invoice",
  sale_booking: "Booking sale",
  partner_cancel_booking: "Partner cancel · booking",
  partner_bulk_cancel: "Partner bulk-cancel · bookings",
  partner_bulk_cancel_weekend: "Partner bulk-cancel · weekend",
  partner_force_cancel: "Partner force-cancel · booking",
  partner_reset: "Partner reset · all data",
  partner_self_reset: "Partner self-reset · all data",
  office: "Office",
};

const PRIMARY_TONE = {
  program_booking: "bg-emerald-50 text-emerald-800 border-emerald-200",
  weekend_booking: "bg-sky-50 text-sky-800 border-sky-200",
  arrival: "bg-amber-50 text-amber-800 border-amber-200",
  trip: "bg-indigo-50 text-indigo-800 border-indigo-200",
  cform: "bg-rose-50 text-rose-800 border-rose-200",
  room_allocation: "bg-purple-50 text-purple-800 border-purple-200",
  issue: "bg-red-50 text-red-800 border-red-200",
  task: "bg-slate-100 text-slate-700 border-slate-200",
  partner: "bg-teal-50 text-teal-800 border-teal-200",
  sale_standalone: "bg-yellow-50 text-yellow-800 border-yellow-200",
  sale_booking: "bg-yellow-50 text-yellow-800 border-yellow-200",
  partner_cancel_booking: "bg-red-50 text-red-800 border-red-200",
  partner_bulk_cancel: "bg-red-50 text-red-800 border-red-200",
  partner_bulk_cancel_weekend: "bg-red-50 text-red-800 border-red-200",
  partner_force_cancel: "bg-red-50 text-red-800 border-red-200",
  partner_reset: "bg-red-100 text-red-900 border-red-300",
  partner_self_reset: "bg-red-100 text-red-900 border-red-300",
  office: "bg-indigo-50 text-indigo-800 border-indigo-200",
};

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function BundlePreview({ bundle, onClose, onRestore, onPurge }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="recycle-preview">
        <div className="p-5 border-b border-[color:var(--aii-border)] flex items-start justify-between gap-3 sticky top-0 bg-white z-10">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Deleted bundle</div>
            <h2 className="font-display font-semibold text-lg truncate">{bundle.primary_label || "Untitled"}</h2>
            <div className="text-xs text-[color:var(--aii-muted)] mt-1 flex flex-wrap items-center gap-3">
              <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded border ${PRIMARY_TONE[bundle.primary_type] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                {PRIMARY_LABEL[bundle.primary_type] || bundle.primary_type}
              </span>
              <span className="inline-flex items-center gap-1"><User size={11}/> {bundle.deleted_by_name || "System"} ({bundle.deleted_by_role || "?"})</span>
              <span className="inline-flex items-center gap-1"><Clock size={11}/> {fmtDate(bundle.deleted_at)}</span>
              <span className="inline-flex items-center gap-1"><Layers size={11}/> {bundle.doc_count || (bundle.docs || []).length} row(s)</span>
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-[color:var(--aii-muted)] hover:text-[color:var(--aii-text)]"><X size={16}/></button>
        </div>
        <div className="p-5 space-y-3">
          {(bundle.docs || []).map((entry, i) => {
            // Prefer the stored doc.id when present — it stays stable across
            // renders even when the parent re-orders the bundle. Falls back
            // to `collection-index` for legacy rows that predate the id
            // rollout.
            const rowKey = (entry.doc && entry.doc.id) ? `${entry.collection}:${entry.doc.id}` : `${entry.collection}:${i}`;
            return (
            <div key={rowKey} className="border border-[color:var(--aii-border)] rounded-lg overflow-hidden">
              <div className="bg-[color:var(--aii-green-tint)] px-3 py-1.5 text-xs font-semibold text-brand-dark flex items-center justify-between">
                <span>{entry.collection}</span>
                <span className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)]">
                  id: {(entry.doc || {}).id ? String(entry.doc.id).slice(0, 8) : "—"}
                </span>
              </div>
              <pre className="text-[11px] leading-tight p-3 overflow-x-auto max-h-64 whitespace-pre-wrap break-words">
                {JSON.stringify(entry.doc, null, 2)}
              </pre>
            </div>
            );
          })}
        </div>
        <div className="p-4 border-t border-[color:var(--aii-border)] flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={() => onPurge(bundle)} data-testid="recycle-purge-btn"
            className="text-sm px-3 py-1.5 border border-red-200 text-red-700 rounded hover:bg-red-50 flex items-center gap-1.5">
            <Trash2 size={13}/> Purge permanently
          </button>
          <button onClick={() => onRestore(bundle)} data-testid="recycle-restore-btn"
            className="text-sm px-3 py-1.5 bg-brand text-white rounded hover:bg-brand-dark flex items-center gap-1.5">
            <Undo2 size={13}/> Restore all
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RecycleBin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null); // full bundle
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");
  // Bulk-select state — Set of bundle ids the Founder ticked. Reset on
  // load + on filter change so nothing outside the visible list is
  // silently included in a purge.
  const [selected, setSelected] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/deleted-items");
      setRows(data || []);
      setSelected(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load Recycle Bin");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { setSelected(new Set()); }, [filter]);

  const openPreview = async (id) => {
    try {
      const { data } = await api.get(`/deleted-items/${id}`);
      setPreview(data);
    } catch (e) { toast.error("Could not load bundle preview"); }
  };

  const restore = async (bundle) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/deleted-items/${bundle.id}/restore`);
      toast.success(`Restored ${data.restored} row(s)${data.skipped ? ` · skipped ${data.skipped}` : ""}`);
      setPreview(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
    setBusy(false);
  };

  const purge = async (bundle) => {
    if (!window.confirm(`Permanently delete "${bundle.primary_label || 'this bundle'}"? This CANNOT be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/deleted-items/${bundle.id}/purge`);
      toast.success("Bundle purged permanently");
      setPreview(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Purge failed"); }
    setBusy(false);
  };

  const filtered = useMemo(
    () => rows.filter(r => filter === "all" ? true : r.primary_type === filter),
    [rows, filter],
  );
  const types = Array.from(new Set(rows.map(r => r.primary_type)));

  // ------------------------------------------------------------
  // Bulk-select helpers — every checkbox on the page reads/writes
  // through the `selected` Set so the "select all" toggle stays in
  // sync with per-row ticks.
  // ------------------------------------------------------------
  const allVisibleSelected = filtered.length > 0 && filtered.every(r => selected.has(r.id));
  const someVisibleSelected = filtered.some(r => selected.has(r.id)) && !allVisibleSelected;

  const toggleRow = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllVisible = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) filtered.forEach(r => next.delete(r.id));
      else filtered.forEach(r => next.add(r.id));
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const purgeBulk = async () => {
    const ids = filtered.filter(r => selected.has(r.id)).map(r => r.id);
    if (ids.length === 0) { toast.error("Nothing selected"); return; }
    const n = ids.length;
    if (!window.confirm(
      `Permanently delete ${n} bundle${n === 1 ? "" : "s"} from the Recycle Bin?\n\n` +
      `This will remove ${n} snapshot${n === 1 ? "" : "s"} FOREVER — they can no longer be restored. This CANNOT be undone.`,
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/deleted-items/purge-bulk", { bundle_ids: ids });
      toast.success(`Purged ${data.purged} bundle${data.purged === 1 ? "" : "s"} permanently`);
      setPreview(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk purge failed"); }
    setBusy(false);
  };

  return (
    <div className="space-y-5">
      <div>
        <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Founder-only</div>
        <h1 className="text-2xl sm:text-3xl font-semibold flex items-center gap-2">
          <ShieldAlert className="text-brand" /> Recycle Bin
        </h1>
        <p className="text-sm text-[color:var(--aii-text-2)] mt-1">Every deleted booking, tour, arrival, task, C-form and partner lands here — audit who removed what, and restore in one click.</p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => setFilter("all")} data-testid="recycle-filter-all"
          className={`text-xs px-3 py-1.5 rounded border ${filter === "all" ? "bg-brand text-white border-brand" : "border-[color:var(--aii-border)] hover:bg-[color:var(--aii-green-tint)]"}`}>
          All ({rows.length})
        </button>
        {types.map(t => (
          <button key={t} onClick={() => setFilter(t)} data-testid={`recycle-filter-${t}`}
            className={`text-xs px-3 py-1.5 rounded border ${filter === t ? "bg-brand text-white border-brand" : "border-[color:var(--aii-border)] hover:bg-[color:var(--aii-green-tint)]"}`}>
            {PRIMARY_LABEL[t] || t} ({rows.filter(r => r.primary_type === t).length})
          </button>
        ))}
      </div>

      {selected.size > 0 && (
        <div className="aii-card p-3 border-2 border-red-300 bg-red-50 flex items-center justify-between gap-3 sticky top-2 z-10" data-testid="recycle-bulk-bar">
          <div className="text-sm text-red-900 flex items-center gap-2">
            <CheckSquare size={16} className="text-red-700"/>
            <span><b>{selected.size}</b> bundle{selected.size === 1 ? "" : "s"} selected</span>
            <button onClick={clearSelection} data-testid="recycle-clear-selection"
                    className="text-[11px] underline text-red-800 hover:text-red-950 ml-2">Clear</button>
          </div>
          <button onClick={purgeBulk} disabled={busy} data-testid="recycle-bulk-purge"
                  className="px-3 py-1.5 bg-red-600 text-white text-sm font-semibold rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-1.5">
            <Trash2 size={14}/> Delete {selected.size} permanently
          </button>
        </div>
      )}

      {loading ? (
        <div className="aii-card p-10 text-center text-[color:var(--aii-muted)]"><Loader2 className="mx-auto animate-spin"/> Loading recycle bin…</div>
      ) : filtered.length === 0 ? (
        <div className="aii-card p-10 text-center text-[color:var(--aii-muted)]" data-testid="recycle-empty">
          Nothing has been deleted{filter === "all" ? " yet" : ` in this category`}. Deleted records will appear here for restore.
        </div>
      ) : (
        <div className="aii-card overflow-x-auto">
          <table className="aii-table">
            <thead>
              <tr>
                <th className="w-8">
                  <button onClick={toggleAllVisible} data-testid="recycle-select-all"
                          title={allVisibleSelected ? "Deselect all visible" : "Select all visible"}
                          className="p-0.5 hover:bg-[color:var(--aii-green-tint)] rounded">
                    {allVisibleSelected
                      ? <CheckSquare size={16} className="text-brand"/>
                      : someVisibleSelected
                        ? <CheckSquare size={16} className="text-brand opacity-60"/>
                        : <Square size={16} className="text-[color:var(--aii-muted)]"/>}
                  </button>
                </th>
                <th>Type</th>
                <th>Label</th>
                <th>Deleted by</th>
                <th>Deleted at</th>
                <th className="text-right">Rows</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} data-testid={`recycle-row-${r.id}`} className={selected.has(r.id) ? "bg-red-50/60" : ""}>
                  <td>
                    <button onClick={() => toggleRow(r.id)} data-testid={`recycle-select-${r.id}`}
                            className="p-0.5 hover:bg-[color:var(--aii-green-tint)] rounded">
                      {selected.has(r.id) ? <CheckSquare size={16} className="text-brand"/> : <Square size={16} className="text-[color:var(--aii-muted)]"/>}
                    </button>
                  </td>
                  <td>
                    <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded border ${PRIMARY_TONE[r.primary_type] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                      {PRIMARY_LABEL[r.primary_type] || r.primary_type}
                    </span>
                  </td>
                  <td className="max-w-[380px]"><div className="truncate" title={r.primary_label}>{r.primary_label}</div>
                    {r.discard_reason && (
                      <div className="text-[10px] text-red-700 italic mt-0.5 truncate" title={r.discard_reason} data-testid={`recycle-reason-${r.id}`}>
                        Reason: {r.discard_reason}
                      </div>
                    )}
                  </td>
                  <td className="text-xs">
                    <div className="font-medium">{r.deleted_by_name || "System"}</div>
                    <div className="text-[color:var(--aii-muted)]">{r.deleted_by_role || ""}</div>
                  </td>
                  <td className="text-xs text-[color:var(--aii-text-2)]">{fmtDate(r.deleted_at)}</td>
                  <td className="text-right text-xs font-semibold">{r.doc_count}</td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openPreview(r.id)} data-testid={`recycle-preview-${r.id}`}
                        className="text-[11px] px-2 py-1 border border-[color:var(--aii-border)] rounded hover:bg-[color:var(--aii-green-tint)]">
                        Preview
                      </button>
                      <button onClick={() => restore(r)} disabled={busy} data-testid={`recycle-restore-${r.id}`}
                        className="text-[11px] px-2 py-1 bg-brand text-white rounded hover:bg-brand-dark flex items-center gap-1 disabled:opacity-50">
                        <Undo2 size={11}/> Restore
                      </button>
                      <button onClick={() => purge(r)} disabled={busy} data-testid={`recycle-purge-${r.id}`}
                        className="text-[11px] px-2 py-1 border border-red-200 text-red-700 rounded hover:bg-red-50 flex items-center gap-1 disabled:opacity-50">
                        <Trash2 size={11}/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {preview && <BundlePreview bundle={preview} onClose={() => setPreview(null)} onRestore={restore} onPurge={purge}/>}
    </div>
  );
}
